import { Component, Input, OnChanges } from '@angular/core';
import { ColorPalette, PageSection } from '../../../models/end-client-reporting-package';
import { ChartPoint, ChartSeries, ReportBlock, tint } from '../report-aggregation';
import type { BlockPatch } from '../report-design-edit';
import { categoriesOf } from './row-categories';

/** How far a chip's background is taken toward white from the colour of the side that won it. */
const CHIP_TINT = 0.85;

/**
 * How far short of 100% a row's answers may fall before it is not a forced choice.
 *
 * The module rests on one invariant: a row's options are alternatives, so they account for everyone
 * who answered. That is what lets two bars grow from a shared centre and be read as "this one
 * instead of that one" -- on a question where a respondent can pick both or neither, the same
 * picture asserts a trade-off the survey never put to anyone. A point of slack for rounding, and no
 * more.
 */
const FORCED_CHOICE_TOLERANCE = 1;

/** What the margin column is called when the block does not say. */
const DEFAULT_GAP_LABEL = 'Preference gap';

/** One end of a trade-off on one row. */
interface Pole {
  label: string;
  percent: number;
  /** Rounded once, here, so the bar and the printed figure can never disagree. */
  text: string;
  wins: boolean;
  /**
   * What hovering the half shows, in the parts a tooltip lays out -- the bar is HTML and has no
   * chart tooltip of its own, so it draws one shaped like theirs.
   */
  tip: { answer: string; percent: string; count: string };
}

/** One question of the battery. */
interface Pair {
  question: string;
  /**
   * Its `variableKeys` entry, which is what a category owns -- the question text repeats down a
   * battery, so the label cannot tell two rows apart. Filled in by the caller, which is the only
   * place that knows a row's index.
   */
  key?: string;
  /**
   * Set on the first row of a group only, carrying how many rows to span.
   *
   * Emitted with the row rather than wrapped around the group: the module is one grid, and the bars
   * only meet the centre in the same place while every row resolves against the same columns.
   */
  rail?: { label: string; span: number; colour: string; first: boolean };
  base: number;
  left: Pole;
  right: Pole;
  /**
   * Everything asked between the two poles. Reported under the row rather than drawn: with both
   * bars growing outward from a fixed centre there is no middle of the bar left to put it in, and
   * a centre that moved to make room for it would cost the comparison the layout exists for.
   */
  neutralText: string;
  /** The margin in points, unsigned; the chip's colour says which way it leans. */
  gap: string;
  gapTip: string;
  leans: 'left' | 'right' | 'level';
  /** The chip, coloured to the side that took the row. */
  gapFill: string;
  gapInk: string;
  /** Read by a screen reader in place of the bar, which is decorative. */
  summary: string;
  /** Why this row cannot be drawn, or how it differs from the headings above it. */
  note: string;
}

/**
 * Head-to-head preference: a battery of trade-offs, one bar per question, each running from one
 * option on the left to the competing option on the right.
 *
 * The card title sits above the module, drawn by the block host like every other one; the header
 * line here names the columns only.
 *
 * Drawn in CSS rather than Highcharts, unlike every other chart block here. A diverging bar whose
 * ends carry headings of their own, plus a margin column beside it, is most of a layout problem and
 * very little of a charting one -- the arithmetic is two percentages and a subtraction -- and the
 * things that matter most here are the 50% centre line and headings that wrap without shoving the
 * bars around, both of which are ordinary CSS and both of which are a fight in Highcharts.
 */
@Component({
  selector: 'app-head-to-head-block',
  standalone: false,
  template: `
    <div class="h2h" [class.compact]="compact" [class.no-gap]="!showGap"
         [class.categorised]="categorised" [class.is-editing]="editing">
      <!-- The header names the columns; the rows carry their own two options. -->
      <div class="head">
        <!-- Holds the rail's column open across the header, so the bars begin where the rows' do. -->
        @if (categorised) {
          <span aria-hidden="true"></span>
        }
        <!-- The two titles flank the centre note rather than the outside edges, so they sit
             symmetrically about the line both bars are measured from. -->
        <span class="axis">
          <!-- Written in place while editing: they are the card's own words, not a setting. -->
          <span class="side left" [style.color]="leftFill">
            @if (editing) {
              <input type="text" class="ecr-inline-input" maxlength="60"
                     aria-label="Left column heading" placeholder="Left column heading"
                     [value]="leftText" (input)="set('leftLabel', $any($event.target).value)">
            } @else {
              {{ leftHeading }}
            }
          </span>
          <!-- What the centre line means, said once. Smaller and grey on purpose: it annotates the
               axis rather than naming a third thing being compared. -->
          <span class="centre-note grey4">50/50 split</span>
          <span class="side right" [style.color]="rightFill">
            @if (editing) {
              <input type="text" class="ecr-inline-input" maxlength="60"
                     aria-label="Right column heading" placeholder="Right column heading"
                     [value]="rightText" (input)="set('rightLabel', $any($event.target).value)">
            } @else {
              {{ rightHeading }}
            }
          </span>
        </span>
        @if (showGap) {
          <span class="gap-head">
            @if (editing) {
              <input type="text" class="ecr-inline-input" maxlength="40"
                     aria-label="Name of the margin column" [placeholder]="defaultGapLabel"
                     [value]="gapText" (input)="set('gapLabel', $any($event.target).value)">
            } @else {
              {{ gapHeading }}
            }
            <!-- The unit, said once in the heading rather than repeated in every chip. -->
            <span class="unit grey4">(percentage points)</span>
          </span>
        } @else {
          <!-- Fills the header's last column where the margin's heading would be. The header is
               placed into the module's one grid like every row, and its centre note sits in fixed
               columns: left empty, the column after it took the first row's first cell, and every
               row after that slid one column along. -->
          <span aria-hidden="true"></span>
        }
      </div>

      @for (pair of pairs; track pair.question) {
        <div class="pair">
          <!-- The group's name in a column of its own, spanning its rows. Emitted with the first row
               of each group rather than as a wrapper, because the module is one grid and has to stay
               one: the bars only meet the centre in the same place while every row resolves against
               the same columns, and a grid per group would let two groups agree on nothing. -->
          <!-- A rule across the module between groups, and the group's name against a bar of its
               own colour. The colour is on the rail only, never on a bar: the bars already say which
               side of the trade-off won, and that is the one thing a second hue here could be
               mistaken for. On the rail it is a label, and it matches the same category drawn on a
               ranked driver elsewhere in the deck. -->
          @if (pair.rail && !pair.rail.first) {
            <span class="group-rule" aria-hidden="true"></span>
          }
          @if (pair.rail) {
            <span class="rail"
                  [style.grid-row]="'span ' + pair.rail.span"
                  [style.border-left-color]="pair.rail.colour"
                  [style.color]="pair.rail.colour">{{ pair.rail.label }}</span>
          }
          @if (!pair.left.label) {
            <span class="note grey4 spans">{{ pair.note }}</span>
          } @else {
            <!-- Each row is its own trade-off, so it carries its own two options at the outside
                 edges. The question text is not drawn at all: on a battery it is the same sentence
                 every row, and the pair itself is what identifies the row. -->
            <span class="option left grey5" [title]="pair.left.label">{{ pair.left.label }}</span>

            <span class="share left" [style.color]="leftFill" [class.faded]="!pair.left.wins">
              {{ pair.left.text }}
            </span>

            <!-- Two halves meeting at a fixed centre, each scaled against its own half. A bar whose
                 origin moves with the split cannot be compared with the row above it; a fixed
                 centre makes "which side, and by how far" one glance down the module. -->
            <!-- The tooltips are drawn, so they are not read: the row's summary below says the
                 same thing to a screen reader. -->
            <span class="half left">
              <span class="fill" [style.width.%]="pair.left.percent"
                    [style.background]="leftFill" [class.faded]="!pair.left.wins"></span>
              <span class="tip" aria-hidden="true" [style.borderColor]="leftFill">
                <span class="tip-head">{{ pair.left.tip.answer }}</span>
                <span class="tip-body">
                  <span class="tip-dot" [style.color]="leftFill">●</span><b>{{ pair.left.tip.percent }}</b>
                </span>
                <span class="tip-foot">{{ pair.left.tip.count }}</span>
              </span>
            </span>
            <span class="divider" aria-hidden="true"></span>
            <span class="half right">
              <span class="fill" [style.width.%]="pair.right.percent"
                    [style.background]="rightFill" [class.faded]="!pair.right.wins"></span>
              <span class="tip" aria-hidden="true" [style.borderColor]="rightFill">
                <span class="tip-head">{{ pair.right.tip.answer }}</span>
                <span class="tip-body">
                  <span class="tip-dot" [style.color]="rightFill">●</span><b>{{ pair.right.tip.percent }}</b>
                </span>
                <span class="tip-foot">{{ pair.right.tip.count }}</span>
              </span>
            </span>

            <span class="share right" [style.color]="rightFill" [class.faded]="!pair.right.wins">
              {{ pair.right.text }}
            </span>

            <span class="option right grey5" [title]="pair.right.label">{{ pair.right.label }}</span>

            @if (showGap) {
              <!-- A chip rather than a bare figure, matching the stacked distribution's net column:
                   one fixed width at the bar's own height, so the margins read as a column of their
                   own. Unlike the net's red/green, the colour here is the side that won, because
                   that is the whole of what the number says. -->
              <span class="gap" [title]="pair.gapTip"
                    [style.background]="pair.gapFill" [style.color]="pair.gapInk">
                {{ pair.gap }}
              </span>
            }

            <span class="sr-only">{{ pair.summary }}</span>
            @if (pair.note) {
              <span class="note grey4 spans">{{ pair.note }}</span>
            }
          }
        </div>
      }
    </div>
  `,
  styles: [`
    /* A container, so the grid can tighten to the width the module is actually given -- a half-
       width segment is narrow however wide the screen is. */
    :host { display: block; container: h2h / inline-size; }

    /* One grid for the whole module, so every bar meets the centre in the same place and both
       columns of figures line up however long an option runs.
       The tracks are named once as properties and every variant below assembles the same list from
       them, so a width change is made in one place rather than once per combination of classes. */
    .h2h {
      --h2h-rail: 8.5rem;
      --h2h-option: minmax(7rem, 15rem);
      --h2h-half: minmax(3rem, 1fr);
      display: grid;
      grid-template-columns:
        var(--h2h-option)     /* the left option */
        auto                  /* its figure */
        var(--h2h-half)       /* its half of the bar */
        0                     /* the centre line */
        var(--h2h-half)       /* the right half */
        auto                  /* its figure */
        var(--h2h-option)     /* the right option */
        auto;                 /* the margin */
      align-items: center;
      /* Tight, so the bars sit close to the line they are measured from; the columns that need
         more air than this ask for it themselves. */
      gap: 8px 6px;
      font-size: 13px;
    }
    .h2h.compact {
      --h2h-rail: 6.5rem;
      --h2h-option: minmax(5rem, 10rem);
      font-size: 11px;
      gap: 6px 4px;
    }
    /* Narrow, the options stop taking their full width before the bars get any. A capped option
       column grows to its cap ahead of a flexible one, so at half width the text kept 15rem a side
       and left the bars -- the thing being compared -- a sliver. Here the two share the room. */
    @container h2h (max-width: 760px) {
      .h2h {
        --h2h-rail: 6.5rem;
        --h2h-option: minmax(4.5rem, 1fr);
      }
    }

    /* The margin column has to leave the grid with its cells, not just stop being drawn.
       The head and the pairs are display:contents, so their children are auto-placed into this one
       grid -- a row that emits seven cells into an eight-column grid leaves the eighth empty and
       pushes the next row's first cell into it, and every row after that slides one further along. */
    .h2h.no-gap {
      grid-template-columns:
        var(--h2h-option) auto var(--h2h-half) 0 var(--h2h-half) auto var(--h2h-option);
    }

    /* Grouped, a column opens on the left for the group's name. A fixed width rather than a
       content-sized one: it is the same name down the whole module, and letting the longest set the
       width would move every bar sideways when one group is renamed. */
    .h2h.categorised {
      grid-template-columns: var(--h2h-rail)
        var(--h2h-option) auto var(--h2h-half) 0 var(--h2h-half) auto var(--h2h-option) auto;
    }
    .h2h.categorised.no-gap {
      grid-template-columns: var(--h2h-rail)
        var(--h2h-option) auto var(--h2h-half) 0 var(--h2h-half) auto var(--h2h-option);
    }

    /* The explicit places in the header all shift one column along with it. */
    .categorised .head .axis { grid-column: 3 / 8; }
    .categorised .head .gap-head { grid-column: 9; }

    /* Set against the rows it names rather than above them, and quiet: it groups the battery, it is
       not a reading of it. */
    .rail {
      grid-column: 1;
      align-self: stretch;
      display: flex;
      align-items: center;
      border-left: 3px solid;
      font-size: 0.9em;
      font-weight: 700;
      letter-spacing: 0.3px;
      line-height: 1.25;
      padding: 6px 12px 6px 10px;
      text-transform: uppercase;
    }
    /* A full-width hairline between one group and the next, with the air either side that actually
       does the separating -- the rule only says where. Not above the first group, which the header
       already sits over. */
    .group-rule {
      grid-column: 1 / -1;
      height: 1px;
      background: var(--ecr-line, #dbe4e8);
      margin: 10px 0 2px;
    }
    .h2h.compact .group-rule { margin: 7px 0 1px; }

    .head, .pair { display: contents; }

    /* The column titles share one line and one size. */
    .head .side,
    .head .gap-head {
      font-size: 14px;
      font-weight: 600;
      color: var(--ecr-ink, #1b2b33);
      line-height: 1.3;
      padding-bottom: 2px;
      align-self: end;
    }
    .h2h.compact .head .side,
    .h2h.compact .head .gap-head { font-size: 12px; }
    /* The deck's dark text rather than Navigator's navy, which belongs to no report. */
    .head .gap-head { color: var(--ecr-ink, #1b2b33); }

    /* Spans both figures, both halves and the line between them. Every one of those has a twin
       of the same width on the other side -- the figures share a min-width no percentage outgrows --
       so the middle of this cell is the centre line; two equal tracks around the note then land it
       on the line and put each title the same distance either side of it. The figures are included
       so a title has room to wrap when the bars are short, rather than overflowing and pushing the
       note off the line. */
    .head .axis {
      grid-column: 2 / 7;
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
      align-items: end;
      column-gap: 24px;
      min-width: 0;
    }
    .h2h.compact .head .axis { column-gap: 16px; }
    .head .side.left { justify-self: end; text-align: right; }
    .head .side.right { justify-self: start; }
    /* Full width of their track while editing, so a heading being typed has room before it wraps. */
    .h2h.is-editing .head .side { justify-self: stretch; }
    .head .gap-head { grid-column: 8; margin-left: 8px; text-align: center; max-width: 7rem; }
    .head .gap-head .unit { display: block; font-size: 11px; font-weight: 400; line-height: 1.2; }

    .head .centre-note {
      font-size: 11px;
      font-weight: 400;
      line-height: 1.3;
      white-space: nowrap;
      padding-bottom: 2px;
    }

    /* The options are sentences and the figures beside them are not, so they get their own air
       rather than the grid's: widening the column gap would push the two halves apart as well. */
    /* Two lines, then cut. A trade-off is written as a whole sentence per side, and left to run
       they set the height of the row -- one long pair pushes its bar away from the bars above and
       below it, which are the thing being compared. The full wording stays in the title and in the
       row's screen-reader summary, so nothing is actually lost. */
    .option {
      line-height: 1.3;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      overflow: hidden;
    }
    .option.left { padding-right: 12px; }
    .option.right { text-align: right; padding-left: 12px; }
    .h2h.compact .option.left { padding-right: 8px; }
    .h2h.compact .option.right { padding-left: 8px; }

    .share {
      font-weight: 600;
      white-space: nowrap;
      min-width: 3.4rem;
    }
    .h2h.compact .share { min-width: 2.6rem; }
    /* The figures stand off their bar, so a long one never reads as part of it. */
    .share.left { text-align: right; padding-right: 6px; }
    .share.right { text-align: left; padding-left: 6px; }
    .h2h.compact .share.left { padding-right: 4px; }
    .h2h.compact .share.right { padding-left: 4px; }

    /* No track behind the bars: the unfilled part of a half carries no reading, and a grey box
       around every row competes with the ink that does. */
    .half {
      position: relative;
      display: flex;
      height: 22px;
      min-width: 0;
    }

    /* The hover, shaped like the tooltip every charted module in this deck already shows -- and
       like the ladder's, which draws its own for the same reason: a native title appears after a
       delay, in the operating system's font, and looks nothing like the rest of the deck.
       Sized to its content rather than to the half it sits in, which can be a few rem wide. */
    .tip {
      position: absolute;
      left: 50%;
      bottom: calc(100% + 4px);
      z-index: 5;
      transform: translateX(-50%);
      width: max-content;
      max-width: 16rem;
      visibility: hidden;
      opacity: 0;
      transition: opacity 0.1s ease-out;
      padding: 7px 10px;
      border: 1px solid var(--nav-grey2, #ddd);
      border-radius: 3px;
      background: var(--nav-true-white, #fff);
      box-shadow: 0 1px 5px rgba(0, 0, 0, 0.2);
      text-align: left;
      font-size: 12px;
      font-weight: 400;
      line-height: 1.5;
      color: var(--ecr-ink, #1b2b33);
      pointer-events: none;
    }
    .half:hover .tip { visibility: visible; opacity: 1; }
    .tip-head, .tip-body, .tip-foot { display: block; }
    .tip-head { font-size: 11px; font-weight: 700; }
    .tip-dot { margin-right: 5px; font-size: 14px; line-height: 1; }
    .tip-foot { font-size: 11px; color: var(--nav-grey4, #888); }
    /* Each half reaches back toward the centre by the same amount, so the line sits the same
       distance from both. Done here rather than on the divider: a negative margin there moves the
       border off the track it marks, which left the right half twice as far from the line as the
       left one. */
    .half.left { justify-content: flex-end; margin-right: -4px; }
    .half.right { justify-content: flex-start; margin-left: -4px; }

    /* The centre. A fixed line both bars grow away from, rather than a boundary that moves.
       In its own zero-width column so the "50/50 split" heading lands exactly on it. */
    .divider {
      width: 0;
      height: 28px;
      border-left: 1px dashed var(--nav-grey4, #999);
    }

    .fill { transition: width 0.2s ease; }
    .half.left .fill { border-radius: 3px 0 0 3px; }
    .half.right .fill { border-radius: 0 3px 3px 0; }

    /* Losing sides keep their colour and lose their weight -- a faded bar still has a readable
       length, which a bar swapped to grey does not. */
    .faded { opacity: 0.55; }

    .gap {
      box-sizing: border-box;
      /* Its own column, set apart from the row it measures and centred under its heading. */
      justify-self: center;
      margin-left: 8px;
      width: 62px;
      height: 22px;
      line-height: 22px;
      border-radius: 3px;
      font-size: 12px;
      font-weight: 600;
      text-align: center;
      white-space: nowrap;
    }
    .h2h.compact .head .gap-head { margin-left: 5px; }
    .h2h.compact .gap { margin-left: 5px; width: 48px; font-size: 11px; }

    /* Notes run under the whole row rather than in a column, because they are sentences. */
    .note.spans { grid-column: 1 / -1; font-size: 0.85em; line-height: 1.3; }
  `],
})
export class HeadToHeadBlockComponent implements ReportBlock, OnChanges {
  @Input({ required: true }) block!: PageSection;
  @Input({ required: true }) series!: ChartSeries[];
  @Input({ required: true }) palette!: ColorPalette;
  @Input() compact?: boolean;
  /** Set while the deck is in edit mode, when the column headings are written in place. */
  @Input() editing?: boolean;
  @Input() onPatch?: (patch: BlockPatch) => void;

  readonly defaultGapLabel = DEFAULT_GAP_LABEL;
  /** The headings exactly as stored, so an input being cleared is not refilled under the cursor. */
  leftText = '';
  rightText = '';
  gapText = '';

  pairs: Pair[] = [];
  /** Whether the rows are gathered into named groups, which opens a column to the left of them. */
  categorised = false;
  leftHeading = '';
  rightHeading = '';
  gapHeading = DEFAULT_GAP_LABEL;
  showGap = true;
  leftFill = '';
  rightFill = '';

  ngOnChanges() {
    this.build();
  }

  set(field: 'leftLabel' | 'rightLabel' | 'gapLabel', value: string) {
    this.onPatch?.({ [field]: value });
  }

  private build() {
    if (this.block.reportType !== 'diverging-bars') return;
    const extra = this.block.reportExtraData;
    const flipped = extra.leftAnswer === 'last';

    this.leftFill = this.palette.primary;
    this.rightFill = this.palette.secondary;
    this.gapHeading = extra.gapLabel?.trim() || DEFAULT_GAP_LABEL;
    this.showGap = extra.showGap !== false;

    // Column titles, not answer names: each row now carries its own two options at the outside
    // edges, so there is nothing for these to default to and nothing they could mislabel. Blank
    // simply leaves the column unheaded.
    this.leftHeading = extra.leftLabel?.trim() ?? '';
    this.rightHeading = extra.rightLabel?.trim() ?? '';
    this.leftText = extra.leftLabel ?? '';
    this.rightText = extra.rightLabel ?? '';
    this.gapText = extra.gapLabel ?? '';

    // By position: seriesForBlock builds one series per variableKeys entry, in order.
    const keys = extra.variableKeys ?? [];
    this.pairs = this.grouped(
      this.series.map((s, at) => ({ ...this.pairFor(s, flipped), key: keys[at] ?? s.name })),
      extra.categories);
  }

  /**
   * Reorders the rows into category order and marks where each group starts.
   *
   * Rows no category claims follow at the end, unnamed rather than gathered under a heading of their
   * own -- "Other" is a group a reader will try to make sense of, and it is only ever the leftovers.
   * A category naming no drawn row disappears entirely, since a heading over nothing is a question.
   */
  private grouped(pairs: Pair[], spec?: { label: string; items: string[] }[]): Pair[] {
    const named = (spec ?? []).filter(category => category.items?.length);
    this.categorised = false;
    if (!named.length) return pairs;

    // Colours from the shared reading, so a category is the same colour here as on a ranked driver
    // charting the same battery.
    const reading = categoriesOf(spec, pairs.map(pair => pair.key ?? ''), this.palette);
    const taken = new Set<string>();
    const out: Pair[] = [];

    named.forEach(category => {
      const rows = pairs.filter(pair =>
        !!pair.key && category.items.includes(pair.key) && !taken.has(pair.key));
      if (!rows.length) return;
      rows.forEach(pair => taken.add(pair.key!));
      out.push({
        ...rows[0],
        rail: {
          label: category.label,
          span: rows.length,
          colour: reading.colourOf(rows[0].key ?? '') ?? 'var(--ecr-grey, #c3ced3)',
          first: out.length === 0,
        },
      }, ...rows.slice(1));
    });

    this.categorised = out.length > 0;
    return [...out, ...pairs.filter(pair => !pair.key || !taken.has(pair.key))];
  }

  private pairFor(s: ChartSeries, flipped: boolean): Pair {
    const empty: Pole = {
      label: '', percent: 0, text: '', wins: false, tip: { answer: '', percent: '', count: '' },
    };
    const blank = (note: string): Pair => ({
      question: s.name,
      base: s.base,
      gapTip: '',
      left: empty,
      right: empty,
      neutralText: '',
      gap: '',
      leans: 'level',
      gapFill: '',
      gapInk: '',
      summary: '',
      note,
    });

    if (!s.known) return blank(`No data for ${s.name}.`);

    // A multi-punch question's options are not alternatives -- a respondent can select several, so
    // no two of them are "this instead of that". Caught by what the question *is* rather than by
    // its arithmetic, which is the more honest test of the two.
    if (s.multiResponse) {
      return blank(`${s.name} accepts more than one answer, so its options are not alternatives. `
        + 'Each diverging bars row needs a single-choice question.');
    }

    // Real answers only. "Not asked" is the share of a compare column that never saw the question
    // and carries optionIndex -1; left in, it would sit at the end of the list and be taken for the
    // right-hand pole, which is the one mistake here that would invert a finding rather than
    // degrade it.
    const asked = s.points.filter(point => point.optionIndex >= 0);
    if (asked.length < 2) {
      return blank(`${s.name} has only one answer, so there are no alternatives to weigh. `
        + 'Each diverging bars row needs a single-choice question.');
    }
    if (s.base === 0) return blank(`Nobody answered ${s.name}.`);

    // Every point, including "Not asked" -- a compare column where some respondents never saw the
    // question is legitimately short of 100% across the answers alone, and that share is not
    // missing, it is accounted for. Anything still short of 100 here is a question whose answers do
    // not account for the people who answered it, which is not a forced choice.
    const accounted = s.points.reduce((sum, point) => sum + point.percent, 0);
    if (accounted < 100 - FORCED_CHOICE_TOLERANCE) {
      return blank(`${s.name} does not add up to 100%, so its answers are not alternatives. `
        + 'Each diverging bars row needs a single-choice question.');
    }

    // The poles are the first and last answers as asked, and everything between them is middle
    // ground. That reads correctly for a straight pair and for a pole / hedge / pole shape alike.
    const first = asked[0];
    const last = asked[asked.length - 1];
    const middle = asked.slice(1, -1);

    const leftPoint = flipped ? last : first;
    const rightPoint = flipped ? first : last;

    const left = this.poleFor(leftPoint, rightPoint, s.base);
    const right = this.poleFor(rightPoint, leftPoint, s.base);
    const neutralText = middle.map(point => `${point.label} ${this.round(point)}%`).join(', ');

    // Taken off the rounded figures rather than the raw ones, so the margin is the subtraction a
    // reader can do from the two numbers printed on the bar. A gap computed from full precision
    // reads as an error when 38.4 and 61.6 are drawn as 38 and 62 but the gap says 23.
    const margin = Math.abs(this.round(leftPoint) - this.round(rightPoint));
    const leans: 'left' | 'right' | 'level' = margin === 0
      ? 'level'
      : (left.wins ? 'left' : 'right');
    const ink = leans === 'level'
      ? 'var(--nav-grey4, #999)'
      : (leans === 'left' ? this.palette.primary : this.palette.secondary);

    return {
      question: s.name,
      base: s.base,
      left,
      right,
      neutralText,
      gap: margin === 0 ? 'Level' : `+${margin}`,
      gapTip: margin === 0
        ? 'Level: the two sides are within half a point'
        : `${(leans === 'left' ? left : right).label} leads by ${margin} points`,
      leans,
      gapFill: leans === 'level'
        ? 'var(--nav-grey1, #f2f2f2)'
        : tint(leans === 'left' ? this.palette.primary : this.palette.secondary, CHIP_TINT),
      gapInk: ink,
      summary: [
        `n=${s.base}`,
        `${left.label} ${left.text}`,
        neutralText,
        `${right.label} ${right.text}`,
        margin === 0 ? 'Level' : `${margin} point lead`,
      ].filter(Boolean).join('; '),
      note: neutralText ? `Middle ground: ${neutralText}` : '',
    };
  }

  private poleFor(point: ChartPoint, against: ChartPoint, base: number): Pole {
    return {
      label: point.label,
      percent: point.percent,
      text: `${this.round(point)}%`,
      // The bars here are HTML rather than a chart, so nothing gives them a tooltip for free. Same
      // three things a chart's would carry: what was chosen, its share, and how many were asked.
      tip: { answer: point.label, percent: `${this.round(point)}%`, count: `n=${base}` },
      // Strictly greater: a dead heat has no winner, and bolding both sides of one would say the
      // opposite of what a tie means.
      wins: point.percent > against.percent,
    };
  }

  private round(point: ChartPoint): number {
    return Math.round(point.percent);
  }
}
